import { NotificationData } from './types'

export class SimpleNotificationManager {
  private static instance: SimpleNotificationManager
  private isSupported = false
  private permission: NotificationPermission = 'default'
  private scheduledNotifications = new Map<string, number>()
  private notificationDetails = new Map<string, {
    id: string
    title: string
    body: string
    scheduleTime: number
    data?: any
  }>()

  private constructor() {
    this.checkSupport()
  }

  static getInstance(): SimpleNotificationManager {
    if (!SimpleNotificationManager.instance) {
      SimpleNotificationManager.instance = new SimpleNotificationManager()
    }
    return SimpleNotificationManager.instance
  }

  private checkSupport(): void {
    this.isSupported = 'Notification' in window
    if (this.isSupported) {
      this.permission = Notification.permission
    }
  }

  private refreshPermissionStatus(): void {
    if (this.isSupported) {
      this.permission = Notification.permission
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      return 'denied'
    }

    try {
      this.permission = await Notification.requestPermission()
      return this.permission
    } catch (error) {
      console.error('Failed to request notification permission:', error)
      return 'denied'
    }
  }

  async sendNotification(
    data: NotificationData,
    delay: number = 0
  ): Promise<void> {
    if (!this.isSupported || this.permission !== 'granted') {
      console.warn('Notifications not supported or permission not granted')
      return
    }

    if (delay === 0) {
      // Send immediately
      this.showNotification(data)
    } else {
      // Schedule for later
      this.scheduleNotification(data, delay)
    }
  }

  private showNotification(data: NotificationData): void {
    try {
      const notification = new Notification(data.title, {
        body: data.body,
        icon: data.icon || '/icon-192x192.png',
        badge: data.badge,
        tag: data.data?.challengeId, // Group notifications by challenge
        data: data.data || {},
        requireInteraction: false,
        silent: false
      })

      // Handle notification click
      notification.onclick = () => {
        window.focus()
        notification.close()
        
        // Navigate to challenge if challengeId is provided
        if (data.data?.challengeId) {
          window.location.hash = '#/'
        }
      }

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close()
      }, 5000)

      console.log('✅ Local notification sent:', data.title)
    } catch (error) {
      console.error('Failed to show notification:', error)
    }
  }

  private scheduleNotification(data: NotificationData, delay: number): void {
    const notificationId = `notification-${Date.now()}-${Math.random()}`
    const scheduleTime = Date.now() + (delay * 1000)

    // Store notification details
    this.notificationDetails.set(notificationId, {
      id: notificationId,
      title: data.title,
      body: data.body,
      scheduleTime,
      data: data.data
    })

    // Schedule the notification
    const timeoutId = window.setTimeout(() => {
      this.showNotification(data)
      this.scheduledNotifications.delete(notificationId)
      this.notificationDetails.delete(notificationId)
    }, delay * 1000)

    this.scheduledNotifications.set(notificationId, timeoutId)

    console.log(`📅 Notification scheduled for ${delay} seconds:`, data.title)
  }

  private clearScheduledNotification(notificationId: string): void {
    const timeoutId = this.scheduledNotifications.get(notificationId)
    if (timeoutId) {
      clearTimeout(timeoutId)
      this.scheduledNotifications.delete(notificationId)
      this.notificationDetails.delete(notificationId)
    }
  }

  async clearAllScheduledNotifications(): Promise<void> {
    for (const [notificationId] of this.scheduledNotifications) {
      this.clearScheduledNotification(notificationId)
    }
    this.notificationDetails.clear()
    console.log('✅ All scheduled notifications cleared')
  }

  async clearNotificationsForChallenge(challengeId: string): Promise<void> {
    // For web notifications, we can't directly cancel by challengeId
    // But we can clear all and let the system re-schedule
    // In a more sophisticated implementation, we could track challenge-specific timeouts
    console.log(`🗑️ Clearing notifications for challenge: ${challengeId}`)
  }

  getPermissionStatus(): NotificationPermission {
    this.refreshPermissionStatus()
    return this.permission
  }

  isNotificationSupported(): boolean {
    return this.isSupported
  }

  getPlatform(): string {
    return 'web'
  }

  async getPendingNotifications(): Promise<any[]> {
    return Array.from(this.notificationDetails.values())
  }

  // Simple status check
  getStatus() {
    return {
      supported: this.isSupported,
      permission: this.permission,
      scheduledCount: this.scheduledNotifications.size,
      platform: 'web'
    }
  }
}

// Export singleton instance
export const simpleNotificationManager = SimpleNotificationManager.getInstance() 